import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import BrandLogo from '../components/BrandLogo';
import LoginIllustration from '../components/LoginIllustration';
import LoginMobile from '../components/LoginMobile';
import LoginForm from '../components/LoginForm';

const zohoAuthUrl = `${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/auth/zoho`;
const REMEMBER_KEY = 'tychi_login_email';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'no_account') {
      // Naming the address turns "it does not work" into something actionable —
      // usually the person signed in with a different Zoho account.
      const tried = params.get('email');
      toast(tried
        ? `No account found for ${tried}. Check you signed in with your work email, or ask your admin.`
        : 'No account found. Contact your admin to get access.', 'error');
    }
    else if (error?.startsWith('zoho') || error === 'sso_failed') toast('Zoho sign-in failed. Please try again.', 'error');

    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      setEmail(saved);
      setRemember(true);
    }
  }, [toast]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (remember) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      toast(err.response?.data?.message || 'Invalid credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formProps = {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    remember,
    setRemember,
    loading,
    onSubmit: submit,
    zohoAuthUrl,
  };

  return (
    <>
      <div className="login-desktop-only">
        <div className="login-split-page">
          <div className="login-split-blobs" aria-hidden>
            <span className="login-blob login-blob--1" />
            <span className="login-blob login-blob--2" />
            <span className="login-blob login-blob--3" />
          </div>

          <div className="login-split-shell">
            <div className="login-split-left">
              <div className="login-split-brand">
                <BrandLogo variant="mark" className="login-brand-mark" />
                <span className="login-split-brand-name">TYCHI</span>
              </div>

              <h1 className="login-split-hello">Login</h1>
              <p className="login-split-welcome">Welcome to log in to your background management system.</p>

              <LoginForm {...formProps} />
            </div>

            <div className="login-split-right" aria-hidden>
              <LoginIllustration />
            </div>
          </div>
        </div>
      </div>

      <div className="login-mobile-only">
        <LoginMobile {...formProps} />
      </div>
    </>
  );
};

export default Login;
